import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import * as Papa from "papaparse";
import { useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileInput } from "@/components/admin/file-input";
import { FileField } from "@/components/admin/file-field";
import { usePapaParse } from "../misc/usePapaParse";
import sampleCsv from "./companies_import.csv?raw";
import {
  useCompanyImport,
  type CompanyImportSchema,
  type DuplicateMode,
} from "./useCompanyImport";

const SAMPLE_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(sampleCsv)}`;

export const CompanyImportButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Upload className="size-4" />
        Importar CSV
      </Button>
      <CompanyImportDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

const CompanyImportDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const refresh = useRefresh();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CompanyImportSchema[]>([]);
  const [duplicateMode, setDuplicateMode] = useState<DuplicateMode>("update");
  const processBatch = useCompanyImport(duplicateMode);
  const { importer, parseCsv, reset } = usePapaParse<CompanyImportSchema>({
    batchSize: 1,
    processBatch,
  });

  useEffect(() => {
    if (importer.state === "complete") refresh();
  }, [importer.state, refresh]);

  const handleFile = (nextFile: File | null) => {
    setFile(nextFile);
    setPreview([]);
    if (!nextFile) return;
    Papa.parse<CompanyImportSchema>(nextFile, {
      header: true,
      preview: 5,
      skipEmptyLines: true,
      complete: ({ data }) => setPreview(data),
    });
  };

  const close = () => {
    if (importer.state === "running") return;
    reset();
    setFile(null);
    setPreview([]);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar empresas desde CSV</DialogTitle>
        </DialogHeader>

        {importer.state === "idle" && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                La columna <strong>name</strong> es obligatoria. El dominio, si
                existe, se usa para detectar duplicados.{" "}
                <a
                  className="underline"
                  href={SAMPLE_URL}
                  download="empresas_ejemplo.csv"
                >
                  Descargar plantilla
                </a>
              </AlertDescription>
            </Alert>
            <FileInput
              source="csv"
              label="Archivo CSV"
              accept={{ "text/csv": [".csv"] }}
              onChange={handleFile}
            >
              <FileField source="src" title="title" />
            </FileInput>
            <label className="block text-sm">
              Al encontrar duplicados
              <select
                className="mt-1 h-10 w-full border bg-background px-3"
                value={duplicateMode}
                onChange={(event) =>
                  setDuplicateMode(event.target.value as DuplicateMode)
                }
              >
                <option value="update">Actualizar con valores del CSV</option>
                <option value="skip">Ignorar la fila</option>
              </select>
            </label>
            {preview.length > 0 && (
              <div className="overflow-x-auto border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-2">Nombre</th>
                      <th className="p-2">Dominio</th>
                      <th className="p-2">País</th>
                      <th className="p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, index) => (
                      <tr key={`${row.name}-${index}`} className="border-t">
                        <td className="p-2">{row.name}</td>
                        <td className="p-2">{row.domain}</td>
                        <td className="p-2">{row.country}</td>
                        <td className="p-2">{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {importer.state === "running" && (
          <Alert>
            <Loader2 className="animate-spin" />
            <AlertDescription>
              Importando {importer.importCount} de {importer.rowCount}. Errores:{" "}
              {importer.errorCount}.
            </AlertDescription>
          </Alert>
        )}
        {importer.state === "complete" && (
          <Alert>
            <AlertDescription>
              Importacion finalizada. Procesadas: {importer.importCount}.
              Errores: {importer.errorCount}.
            </AlertDescription>
          </Alert>
        )}
        {importer.state === "error" && (
          <Alert variant="destructive">
            <AlertDescription>{importer.error.message}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            onClick={close}
            disabled={importer.state === "running"}
          >
            Cerrar
          </Button>
          {importer.state === "idle" && (
            <Button onClick={() => file && parseCsv(file)} disabled={!file}>
              Importar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
